import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Logo from '../assets/company_logo';
import './Navbar.css';

// Central config → no repeated JSX, easy to maintain
const NAV_GROUPS = [
  {
    label: 'Leistungen',
    links: [
      { to: '/services/web-development', label: 'Web Entwicklung' },
      { to: '/services/app-development', label: 'App Entwicklung' },
      { to: '/services/ui-ux-design', label: 'UI/UX Design' },
    ],
  },
  {
    label: 'Portfolio',
    links: [
      { to: '/portfolio', label: 'Referenzen' },
      { to: '/case-studies', label: 'Case Studies' },
    ],
  },
  {
    label: 'Unternehmen',
    links: [
      { to: '/ueber-uns', label: 'Über uns' },
      { to: '/karriere', label: 'Karriere' },
      { to: '/blog', label: 'Blog' },
    ],
  },
];

const MOBILE_BREAKPOINT = 1100;

const ChevronIcon = () => (
  <svg className="dropdown-icon" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z" />
  </svg>
);

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  // Tracks which dropdown is open on touch/keyboard (desktop hover handled by CSS)
  const [openDropdown, setOpenDropdown] = useState(null);

  const location = useLocation();

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const toggleMobileMenu = useCallback(() => setMobileMenuOpen((v) => !v), []);

  // Close menus + release focus on every route change
  // (fixes the "frozen" dropdown caused by :focus-within staying active)
  useEffect(() => {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [location.pathname]);

  // Lock body scroll while the mobile overlay is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Close the mobile menu automatically when resizing up to desktop
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT + 1}px)`);
    const handle = (e) => {
      if (e.matches) setMobileMenuOpen(false);
    };
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  // Close mobile menu on Escape
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e) => e.key === 'Escape' && setMobileMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileMenuOpen]);

  // Hide-on-scroll-down / show-on-scroll-up, with threshold + always-show near top
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastScrollY) >= 8) {
        setShowNavbar(y < lastScrollY || y < 80);
        lastScrollY = y;
      }
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown + drop focus so :focus-within releases it
  const handleDropdownLinkClick = () => {
    setOpenDropdown(null);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <nav className={`navbar ${showNavbar ? '' : 'navbar--hidden'}`}>
      <div className="nav-logo-section">
        <NavLink to="/" className="nav-home-link" onClick={closeMobileMenu}>
          <Logo className="nav-logo" />
          <span className="nav-title">Nexite</span>
        </NavLink>
      </div>

      {/* Desktop Navigation */}
      <div className="nav-links">
        {NAV_GROUPS.map((group) => {
          const isOpen = openDropdown === group.label;
          return (
            <div
              key={group.label}
              className={`nav-dropdown ${isOpen ? 'is-open' : ''}`}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button
                className="dropdown-button"
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={() => setOpenDropdown(isOpen ? null : group.label)}
              >
                {group.label}
                <ChevronIcon />
              </button>
              <div className="dropdown-menu" role="menu">
                {group.links.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className="dropdown-link"
                    role="menuitem"
                    onClick={handleDropdownLinkClick}
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}

        <NavLink to="/projektanfrage" className="projektanfrage-link">
          Projektanfrage
        </NavLink>
      </div>

      {/* Hamburger (hidden while overlay is open) */}
      {!mobileMenuOpen && (
        <button
          className="mobile-menu-icon"
          onClick={toggleMobileMenu}
          aria-label="Menü öffnen"
          aria-expanded={mobileMenuOpen}
        >
          <svg width="30" height="30" viewBox="0 0 100 80" aria-hidden="true">
            <rect width="100" height="10" />
            <rect y="30" width="100" height="10" />
            <rect y="60" width="100" height="10" />
          </svg>
        </button>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu" role="dialog" aria-modal="true">
          <div className="mobile-menu-header">
            <NavLink to="/" className="nav-home-link" onClick={closeMobileMenu}>
              <Logo className="nav-logo" />
              <span className="nav-title">Nexite</span>
            </NavLink>
            <button
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Menü schließen"
            >
              <svg
                className="mobile-menu-close-icon"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="4" y1="4" x2="20" y2="20" />
                <line x1="20" y1="4" x2="4" y2="20" />
              </svg>
            </button>
          </div>

          <nav className="mobile-menu-links">
            <ul>
              {NAV_GROUPS.map((group) => (
                <li key={group.label} className="mobile-menu-category">
                  <div className="mobile-menu-category-title">{group.label}</div>
                  {group.links.map((link) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      className="mobile-menu-link"
                      onClick={closeMobileMenu}
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </li>
              ))}
              <li>
                <NavLink
                  to="/projektanfrage"
                  className="mobile-projektanfrage-link"
                  onClick={closeMobileMenu}
                >
                  Projektanfrage
                </NavLink>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </nav>
  );
};

export default Navbar;